import React, { Suspense, useRef } from 'react';
import { Grid, GridItem } from '@charlietango/ui';

import {
  SchemaPartyDetails,
  SchemaPartyDetailsResponse,
} from '../../api/api-schema';
import {
  CollectionState,
  ElectionType,
  electionTypeMap,
} from '../../api/api-types';
import { useDictionary } from '../../hooks/useDictionary';
import { colors } from '../../styles/colors';
import { thousandsSeparators } from '../../utils/format-utils';
import Anchor from '../Anchor/Anchor';
import PartyDetailsItem from './PartyDetailsItem';
import Status from './Status';
import type { PartyElectionToggleProps } from './PartyElectionToggle';

export type PartyDetailsProps = {
  party?: SchemaPartyDetailsResponse;
  className?: string;
  children?: React.ReactNode;
  selectedRegion?: 'dk' | 'eu';
  onSelectedRegionChange?: (region: 'dk' | 'eu') => void;
  hideNoCollectionStatus?: boolean;
};

function formatUrl(url: string) {
  if (!/^https?:\/\//.test(url)) {
    // Ensure we add the protocol to the URL
    return `https://${url}`;
  }
  return url;
}

const PartyElectionToggleClient = !process.env.SERVER
  ? process.env.NODE_ENV === 'test'
    ? require('./PartyElectionToggle').default
    : React.lazy(() => import('./PartyElectionToggle'))
  : null;

export function PartyElectionToggle(props: PartyElectionToggleProps) {
  if (PartyElectionToggleClient === null) return null;

  if (process.env.NODE_ENV === 'test') {
    const TestPartyElectionToggleClient = PartyElectionToggleClient as React.ComponentType<
      PartyElectionToggleProps
    >;
    return <TestPartyElectionToggleClient {...props} />;
  }

  return (
    <Suspense fallback={null}>
      <PartyElectionToggleClient {...props} />
    </Suspense>
  );
}

function PartyDetails({
  party,
  className,
  children,
  selectedRegion: controlledSelectedRegion,
  hideNoCollectionStatus = false,
}: PartyDetailsProps) {
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const { t } = useDictionary();

  const selectedRegion =
    controlledSelectedRegion ?? electionTypeMap[ElectionType.Folketingsvalg];

  const currentParty: SchemaPartyDetails =
    party && selectedRegion ? party[selectedRegion] : undefined;

  if (
    !party ||
    !currentParty ||
    currentParty.state === CollectionState.noCollection
  ) {
    return hideNoCollectionStatus ? null : (
      <Status status={CollectionState.noCollection} />
    );
  }

  return (
    <div className={className} ref={dropdownRef}>
      <Grid
        gridColumns={4}
        sx={{
          color: colors.base.Text.PrimaryStatic,
          mt: 0,
          gridTemplateColumns: [
            null,
            null,
            'fit-content(25%) max-content max-content minmax(0, 1fr)',
          ],
        }}
      >
        <GridItem size={[1, 1, 1 / 4]}>
          {party.representative?.name ? (
            /*
              - IF there is only representative, it will be visible in party details as "Repræsentant".
              - IF there is only owner, it will be visible in party details as "Indehaver".
              - IF there are both representative and owner, only "Repræsentant" is visible in party details.
            */
            <PartyDetailsItem
              label={t('party.representative')}
              value={party.representative.name}
            />
          ) : party.owner?.name ? (
            <PartyDetailsItem
              label={t('party.owner')}
              value={party.owner.name}
            />
          ) : null}
        </GridItem>
        <GridItem size={[1, 1, 1 / 4]}>
          {currentParty.voteCount !== undefined ? (
            <PartyDetailsItem
              label={t('party.voteCount')}
              ariaValue={currentParty.voteCount.toString()}
              value={thousandsSeparators(currentParty.voteCount)}
            />
          ) : null}
        </GridItem>
        <GridItem size={[1, 1, 1 / 4]}>
          <PartyDetailsItem
            label="Hjemmeside"
            value={
              currentParty.partyWebsite || party.webSite ? (
                <Anchor
                  external
                  target="_blank"
                  href={formatUrl(
                    currentParty.partyWebsite || party.webSite || '',
                  )}
                  sx={{
                    color: colors.base.Text.Link,
                    textDecoration: 'underline',
                  }}
                >
                  Partiets side
                </Anchor>
              ) : (
                '-'
              )
            }
          />
        </GridItem>
        <GridItem size={[1, 1, 1 / 4]}>
          <PartyDetailsItem
            label={t('party.email')}
            value={
              party.contact?.email ? (
                <Anchor external href={`mailto:${party.contact.email}`}>
                  {party.contact.email}
                </Anchor>
              ) : (
                '-'
              )
            }
          />
        </GridItem>
      </Grid>
      {children}
    </div>
  );
}

export default PartyDetails;
